import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { del, errorMessage, get, post, put } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { money } from "@/lib/plans";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Field = ({ id, label, ...props }) => (
  <div><Label htmlFor={id}>{label}</Label><Input id={id} className="mt-1.5" data-testid={id} {...props} /></div>
);

const BundleDialog = ({ bundle, onClose, onSaved }) => {
  const [f, setF] = useState({ name: "", texts: "", price: "", active: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (bundle) setF(bundle.id ? { name: bundle.name, texts: String(bundle.texts), price: (bundle.price_pence / 100).toFixed(2), active: bundle.active } : { name: "", texts: "", price: "", active: true });
  }, [bundle]);
  const save = async (e) => {
    e.preventDefault();
    const texts = parseInt(f.texts, 10);
    const pence = Math.round(parseFloat(f.price) * 100);
    if (!f.name.trim() || !(texts > 0) || !(pence >= 30)) return toast.error("Enter a name, a number of texts and a price of at least £0.30");
    setBusy(true);
    try {
      const body = { name: f.name.trim(), texts, price_pence: pence, active: f.active };
      await (bundle.id ? put(`/platform/sms-bundles/${bundle.id}`, body) : post("/platform/sms-bundles", body));
      toast.success(bundle.id ? "Bundle updated" : "Bundle added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return (
    <Dialog open={!!bundle} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="bundle-dialog">
        <DialogHeader><DialogTitle className="font-display">{bundle?.id ? "Edit bundle" : "New text bundle"}</DialogTitle></DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4">
          <Field id="bundle-name-input" label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. 100 texts" />
          <div className="grid grid-cols-2 gap-3">
            <Field id="bundle-texts-input" label="Texts" type="number" min="1" value={f.texts} onChange={(e) => setF({ ...f, texts: e.target.value })} />
            <Field id="bundle-price-input" label="Price (£)" type="number" min="0.30" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
          </div>
          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm" htmlFor="bundle-active-switch">
            <span><span className="block font-medium text-slate-900">On sale</span><span className="text-xs text-slate-500">Hidden bundles aren't shown to managers</span></span>
            <Switch id="bundle-active-switch" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="bundle-active-switch" />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="bundle-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="bundle-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdjustDialog = ({ company, onClose, onSaved }) => {
  const [f, setF] = useState({ delta: "", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({ delta: "", reason: "" });
  }, [company]);
  const save = async (e) => {
    e.preventDefault();
    const delta = parseInt(f.delta, 10);
    if (!delta || f.reason.trim().length < 2) return toast.error("Enter a number (use minus to remove) and a reason");
    setBusy(true);
    try {
      const res = await post(`/platform/companies/${company.company_id}/sms-tokens`, { delta, reason: f.reason.trim() });
      toast.success(`${company.name} now has ${res.balance} texts`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
    return undefined;
  };
  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="adjust-tokens-dialog">
        <DialogHeader><DialogTitle className="font-display">Adjust texts for {company?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4">
          <p className="text-sm text-slate-600">Current balance: <span className="font-semibold text-slate-900">{company?.balance}</span></p>
          <Field id="adjust-tokens-delta-input" label="Texts to add (use minus to remove, e.g. -20)" type="number" value={f.delta} onChange={(e) => setF({ ...f, delta: e.target.value })} />
          <Field id="adjust-tokens-reason-input" label="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Goodwill, refund" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="adjust-tokens-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="adjust-tokens-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BundlesCard = ({ data, reload }) => {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const remove = async () => {
    try {
      await del(`/platform/sms-bundles/${deleting.id}`);
      toast.success("Bundle deleted");
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };
  return (
    <section className="max-w-4xl space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sms-bundles-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Text bundles</h2>
          <p className="text-sm text-slate-600">One-off purchases in £, paid on Square&apos;s secure checkout (live). Tokens are added once Square confirms the payment.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-blue-700 hover:bg-blue-800" data-testid="add-bundle-button"><Plus className="mr-1 h-4 w-4" /> New bundle</Button>
      </div>
      {data.bundles.length ? (
        <ul className="divide-y divide-slate-100">
          {data.bundles.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`admin-bundle-${b.id}`}>
              <div>
                <p className="font-semibold text-slate-900">{b.name} {!b.active && <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Hidden</span>}</p>
                <p className="text-sm text-slate-600">{b.texts} texts · {money(b.price_pence / 100)} · {(b.price_pence / b.texts).toFixed(1)}p per text</p>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" aria-label="Edit bundle" onClick={() => setEditing(b)} data-testid={`edit-bundle-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" aria-label="Delete bundle" onClick={() => setDeleting(b)} data-testid={`delete-bundle-${b.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-500" data-testid="no-bundles">No bundles yet. Add one so managers can buy texts.</p>}
      <BundleDialog bundle={editing} onClose={() => setEditing(null)} onSaved={reload} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title={`Delete ${deleting?.name}?`}
        description="Managers won't be able to buy it any more. Past purchases and balances aren't affected." confirmLabel="Delete" onConfirm={remove} />
    </section>
  );
};

const BalancesCard = ({ rows, reload }) => {
  const [adjusting, setAdjusting] = useState(null);
  return (
    <section className="max-w-4xl space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sms-balances-admin">
      <h2 className="font-display text-lg font-bold text-slate-900">Company balances</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3 font-medium">Company</th><th className="py-2 pr-3 font-medium">Texts left</th><th className="py-2 pr-3 font-medium">Sent this month</th><th className="py-2 pr-3 font-medium">Last purchase</th><th /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.company_id} className="border-t border-slate-100" data-testid={`sms-balance-row-${r.company_id}`}>
                <td className="py-2 pr-3 font-medium text-slate-900">{r.name}</td>
                <td className={`py-2 pr-3 font-data ${r.balance <= 10 ? "text-amber-700" : ""}`} data-testid={`sms-balance-${r.company_id}`}>{r.balance}</td>
                <td className="py-2 pr-3 font-data">{r.sent_this_month}</td>
                <td className="py-2 pr-3">{r.last_purchase_at ? formatDate(r.last_purchase_at.slice(0, 10), "dd/MM/yyyy") : "—"}</td>
                <td className="py-2 text-right"><Button size="sm" variant="outline" onClick={() => setAdjusting(r)} data-testid={`adjust-tokens-${r.company_id}`}><SlidersHorizontal className="mr-1 h-4 w-4" /> Adjust</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AdjustDialog company={adjusting} onClose={() => setAdjusting(null)} onSaved={reload} />
    </section>
  );
};

export const SmsTokensAdmin = () => {
  const [bundles, setBundles] = useState(null);
  const [rows, setRows] = useState(null);
  const load = () => {
    get("/platform/sms-bundles").then(setBundles).catch((e) => toast.error(errorMessage(e)));
    get("/platform/sms-tokens").then(setRows).catch((e) => toast.error(errorMessage(e)));
  };
  useEffect(() => {
    load();
  }, []);
  if (!bundles || !rows) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  return (
    <>
      <BundlesCard data={bundles} reload={load} />
      <BalancesCard rows={rows} reload={load} />
    </>
  );
};
